import React, { useEffect, useState } from 'react'
import {
  searchBoothRecords,
  searchResources,
  createBoothRecord,
  updateBoothRecord,
  deleteBoothRecord
} from 'helpers/backend_helper'
import { Container } from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import BoothsTable from './BoothsTable'
import BoothPopup from './BoothModal'
import { toast } from 'react-toastify'
import Pagination from 'Components/Common/Pagination'
import { PERPAGE_DEFAULT } from 'lib/constant'
import { ISort } from 'lib/type'

export interface IBoothRecord {
  _id?: string
  name: string
  locationId: string
  location: Location
  activeActions: any[]
  /** Kabin bazlı bakım (2026-08-24). Boş/eksik = bakım yok. */
  maintenance?: {
    services?: any[]
    startedAt?: string | null
    startedBy?: string | null
  }
  isCash: boolean
  isCreditCard: boolean
  isQrcode: boolean
  currencyId: string
  currency: Currency
  languageId: string
  language: Language
  isPrinterActive: boolean
  isActive: boolean
  iPAddress: string
  notes?: string
  connectionStatus?: {
    status?: number  // 0: OFFLINE, 1: ONLINE, 2: BUSY
    ip?: string
    lastHeartbeat?: string
  }
  hardwareStatus?: {
    printer?: { connected?: boolean; name?: string; error?: string | null; lastCheck?: string }
    camera?: { connected?: boolean; type?: string; lastCheck?: string }
    pos?: { connected?: boolean; error?: string | null; lastCheck?: string }
  }
  // Kabinin kimlik doğrulama durumu (2026-08-17). "ID Status" kolonunu besler.
  // Müşteri talebi: kabinin ID'si devre dışı kaldığında panelden görülebilsin.
  authStatus?: {
    lastAuthAt?: string | null
    lastAuthFailureAt?: string | null
    lastAuthFailureReason?: string | null
  }
}

export interface Location {
  _id: string
  name: string
  city: string
  address: string
}

export interface Currency {
  _id: string
  name: string
  symbol: string
}

export interface Language {
  _id: string
  name: string
  orgName: string
}

const Booths = () => {
  document.title = 'Booth Record | SocialBooth - Admin Dashboard'

  //state data
  const [fetching, setFetching] = useState<boolean>(false)
  const [boothRecords, setBoothRecords] = useState<IBoothRecord[]>([])
  const [openBoothPopup, setOpenBoothPopup] = useState<boolean>(false)
  const [currentBooth, setCurrentBooth] = useState<IBoothRecord>()
  const [editMode, setEditMode] = useState<boolean>(false)
  const [actions, setActions] = useState<any[]>([])
  const [currencies, setCurrencies] = useState<any[]>([])
  const [languages, setLanguages] = useState<any[]>([])
  const [locations, setLocations] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPage, setTotalPage] = useState<number>(1)
  const [valueSearch, setValueSearch] = useState<string>('')
  const [sortAction, setSortAction] = useState<ISort>({ sortBy: '', sortDirection: 'ASC' })
  const [canEdit, setCanEdit] = useState<boolean>(false)
  
  // Polling configuration: 10 seconds for testing, 30 seconds for production
  const POLLING_INTERVAL = process.env.NODE_ENV === 'production' ? 30000 : 10000

  const fetchBoothRecords = async (params: any, showLoading = true) => {
    try {
      if (showLoading) setFetching(true)
      const res = searchBoothRecords(params)
      const response = await res
      const data = response.data

      setBoothRecords(data)
      setTotalPage(response.totalPages)
    } catch (e) {
      const errMsg = e as string
      toast.error(errMsg)
    }

    if (showLoading) setFetching(false)
  }

  useEffect(() => {
    const fetchResources = async (type: string, callback: (arg: any) => void, params: any = {}) => {
      try {
        const res = searchResources(type, params)
        const response = await res
        const data = response.data
        if (data.length) {
          callback(data)
        }
      } catch (e) {
        const errMsg = e as string
        toast.error(errMsg)
      }
    }

    fetchResources('action', setActions, { activeOnly: 'true' })
    fetchResources('currency', setCurrencies)
    fetchResources('language', setLanguages)
    fetchResources('location', setLocations)
    fetchBoothRecords({ pageNo: currentPage, pageSize: PERPAGE_DEFAULT })
  }, [])

  // Polling effect to refresh booth data periodically (silent, no loading spinner)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchBoothRecords(getParams(currentPage, valueSearch), false)
    }, POLLING_INTERVAL)

    return () => clearInterval(intervalId)
  }, [currentPage, valueSearch, POLLING_INTERVAL])

  const handleOpenDetails = (id: string) => {
    const selectedBooth = boothRecords.find((b) => b._id === id)
    if (selectedBooth) {
      setCurrentBooth(selectedBooth)
      setOpenBoothPopup(true)
    }
  }

  const handleCreateOrUpdateBooth = async (params: any) => {
    try {
      const res = editMode ? updateBoothRecord(params, params.id || params._id) : createBoothRecord(params)
      const response = await res
      const data = response.data

      if (response.message === 'SUCCESSFULL') {
        if (editMode) {
          let updateBoothRecords = boothRecords.map((boothRecord: IBoothRecord) => {
            if (boothRecord._id === data._id) {
              return data
            }
            return boothRecord
          })

          setBoothRecords(updateBoothRecords)
        } else {
          setBoothRecords([data, ...boothRecords])
        }
        return response.message
      }

      return response.data
    } catch (e) {
      const errMsg = e as string
      toast.error(errMsg)
      return 'FALSE'
    }
  }

  const handleUpdateBoothNotes = async (id: string, notes: string) => {
    try {
      const response = await updateBoothRecord({ notes }, id)
      const data = response.data

      if (response.message === 'SUCCESSFULL') {
        setBoothRecords((prev) =>
          prev.map((boothRecord) =>
            boothRecord._id === id ? { ...boothRecord, notes: data?.notes ?? notes } : boothRecord
          )
        )
        return 'SUCCESSFULL'
      }
      return response.data
    } catch (e) {
      const errMsg = e as string
      toast.error(errMsg)
      return 'FALSE'
    }
  }

  const handleDeleteBooth = async (id: string) => {
    try {
      const res = deleteBoothRecord(id)
      const response = await res
      if (response.message === 'SUCCESSFULL') {
        setBoothRecords(
          boothRecords.filter((booth) => {
            return booth._id !== id
          })
        )
      }

      return response.message
    } catch (e) {
      const errMsg = e as string
      toast.error(errMsg)
      return 'FALSE'
    }
  }

  const searchBooth = (value: string) => {
    const trimmedValue = value.trim()
    const queryParams = {
      pageNo: 1,
      pageSize: PERPAGE_DEFAULT,
      ...(trimmedValue && { search: trimmedValue })
    }

    fetchBoothRecords(queryParams)
    setValueSearch(trimmedValue)
  }

  useEffect(() => {
    fetchBoothRecords(getParams(currentPage, valueSearch))
  }, [currentPage])

  useEffect(() => {
    if (sortAction.sortBy.length > 0) {
      fetchBoothRecords(getParams(currentPage, valueSearch))
    }
  }, [sortAction])

  const getParams = (page: number = 1, value: string) => {
    return {
      pageNo: page,
      pageSize: PERPAGE_DEFAULT,
      ...(value.trim() && { search: value }),
      ...(sortAction.sortBy.length > 0 && sortAction)
    }
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Booth Record index' pageTitle='Booth Record' />
          <BoothsTable
            boothRecords={boothRecords}
            setOpenBoothPopup={setOpenBoothPopup}
            handleOpenDetails={handleOpenDetails}
            setEditMode={setEditMode}
            actionOptions={actions}
            handleDeleteBooth={handleDeleteBooth}
            handleUpdateBoothNotes={handleUpdateBoothNotes}
            searchBooth={searchBooth}
            fetching={fetching}
            sortAction={sortAction}
            setSortAction={setSortAction}
            setCanEdit={setCanEdit}
            refreshBooths={() =>
              fetchBoothRecords(getParams(currentPage, valueSearch), false)
            }
          />
          <BoothPopup
            isOpen={openBoothPopup}
            actionOptions={actions}
            currencyOptions={currencies}
            languageOptions={languages}
            locationOptions={locations}
            setOpenPopup={setOpenBoothPopup}
            handleCreateOrUpdateBooth={handleCreateOrUpdateBooth}
            boothRecord={currentBooth}
            editMode={editMode}
            canEdit={canEdit}
          />
          {totalPage > 1 && (
            <Pagination
              perPageData={PERPAGE_DEFAULT}
              data={boothRecords}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={totalPage}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}
export default Booths
